import React, { useState, useEffect, useCallback } from 'react';
import { paymentsApi, Payment } from '../../api/payments';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Search, Loader2, Eye, CircleAlert, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/shared/Pagination';
import { ViewDialog, DetailSection, DetailItem } from '../../components/shared/ViewDialog';

interface PaymentsPageProps {
  isDark: boolean;
}

const ITEMS_PER_PAGE = 10;

export default function PaymentsPage({ isDark }: PaymentsPageProps) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingPayment, setViewingPayment] = useState<Payment | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const fetchPayments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: any = { page: currentPage, limit: ITEMS_PER_PAGE };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (providerFilter !== 'all') params.provider = providerFilter;
      const response = await paymentsApi.getAll(params);
      setPayments(response.items);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Не удалось загрузить платежи';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter, providerFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleSearch = (value: string) => { setSearchQuery(value); setCurrentPage(1); };
  const handleStatusFilter = (value: string) => { setStatusFilter(value); setCurrentPage(1); };
  const handleProviderFilter = (value: string) => { setProviderFilter(value); setCurrentPage(1); };

  const handleView = async (payment: Payment) => {
    try {
      const fullPayment = await paymentsApi.getById(payment.id);
      setViewingPayment(fullPayment);
      setIsViewDialogOpen(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось загрузить детали платежа');
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('ru-RU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price: number, currency: string = 'UZS') => {
    return new Intl.NumberFormat('uz-UZ', { style: 'currency', currency, minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      initiated: { label: 'Инициирован', className: 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/50 cursor-pointer' },
      success: { label: 'Успех', className: 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/50 cursor-pointer' },
      failed: { label: 'Ошибка', className: 'bg-red-500/10 text-red-500 hover:bg-red-500/50 cursor-pointer' },
      expired: { label: 'Истёк', className: 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/50 cursor-pointer' },
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-slate-500/10 text-slate-500 hover:bg-slate-500/50 cursor-pointer' };
    return <Badge className={cn('rounded-lg font-medium border-0', statusInfo.className)}>{statusInfo.label}</Badge>;
  };

  const getProviderBadge = (provider: string) => {
    const providerMap: Record<string, { label: string; className: string }> = {
      click: { label: 'Click', className: 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/50 cursor-pointer' },
      payme: { label: 'Payme', className: 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/50 cursor-pointer' },
      paylov: { label: 'Paylov', className: 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/50 cursor-pointer' },
    };
    const providerInfo = providerMap[provider] || { label: provider, className: 'bg-slate-500/10 text-slate-500 hover:bg-slate-500/50 cursor-pointer' };
    return <Badge className={cn('rounded-lg font-medium border-0', providerInfo.className)}>{providerInfo.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>Платежи</h1>
        <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Все платёжные транзакции</p>
      </div>

      <Card className={cn('rounded-2xl border-0 overflow-hidden', isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50')}>
        <div className={cn('p-5 lg:p-6 border-b', isDark ? 'border-slate-700' : 'border-slate-200')}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className={cn('text-lg font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>Все платежи</h3>
              <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>{total} платежей всего</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-1 lg:flex-initial lg:max-w-2xl">
              <div className="relative flex-1">
                <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4', isDark ? 'text-slate-500' : 'text-slate-400')} />
                <Input type="text" placeholder="Поиск платежей..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)}
                  className={cn('pl-10 h-10 rounded-xl border-0', isDark ? 'bg-slate-700 text-white placeholder:text-slate-500' : 'bg-slate-100 text-slate-900')} />
              </div>
              <Select value={statusFilter} onValueChange={handleStatusFilter}>
                <SelectTrigger className={cn('w-full sm:w-[140px] h-10 rounded-xl border-0 cursor-pointer', isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900')}>
                  <SelectValue placeholder="Все статусы" />
                </SelectTrigger>
                <SelectContent className="cursor-pointer">
                  <SelectItem value="all" className="cursor-pointer">Все статусы</SelectItem>
                  <SelectItem value="initiated" className="cursor-pointer">Инициирован</SelectItem>
                  <SelectItem value="success" className="cursor-pointer">Успех</SelectItem>
                  <SelectItem value="failed" className="cursor-pointer">Ошибка</SelectItem>
                  <SelectItem value="expired" className="cursor-pointer">Истёк</SelectItem>
                </SelectContent>
              </Select>
              <Select value={providerFilter} onValueChange={handleProviderFilter}>
                <SelectTrigger className={cn('w-full sm:w-[140px] h-10 rounded-xl border-0 cursor-pointer', isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-900')}>
                  <SelectValue placeholder="Все провайдеры" />
                </SelectTrigger>
                <SelectContent className="cursor-pointer">
                  <SelectItem value="all" className="cursor-pointer">Все провайдеры</SelectItem>
                  <SelectItem value="click" className="cursor-pointer">Click</SelectItem>
                  <SelectItem value="payme" className="cursor-pointer">Payme</SelectItem>
                  <SelectItem value="paylov" className="cursor-pointer">Paylov</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" /></div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <CircleAlert className="h-10 w-10 text-red-500" />
              <p className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>{error}</p>
              <Button variant="outline" onClick={fetchPayments} className={cn('rounded-xl', isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : '')}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Повторить
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>ID платежа</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Провайдер</TableHead>
                  <TableHead className={cn('font-semibold hidden md:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Заказ</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Сумма</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Статус</TableHead>
                  <TableHead className={cn('font-semibold hidden sm:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Создан</TableHead>
                  <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">Платежи не найдены</TableCell></TableRow>
                ) : payments.map((payment) => (
                  <TableRow key={payment.id} className={cn('transition-colors', isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50')}>
                    <TableCell className={cn('font-mono text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{payment.id.substring(0, 8)}...</TableCell>
                    <TableCell>{getProviderBadge(payment.provider)}</TableCell>
                    <TableCell className={cn('hidden md:table-cell font-mono text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{payment.order_id.substring(0, 8)}...</TableCell>
                    <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>{formatPrice(payment.amount, payment.currency)}</TableCell>
                    <TableCell>{getStatusBadge(payment.status)}</TableCell>
                    <TableCell className={cn('hidden sm:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>{formatDate(payment.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" aria-label="Детали платежа" onClick={() => handleView(payment)} className={cn('h-8 px-2 rounded-lg', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100')}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {!isLoading && !error && payments.length > 0 && (
          <Pagination isDark={isDark} currentPage={currentPage} totalPages={totalPages} total={total} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
        )}
      </Card>

      <ViewDialog isDark={isDark} isOpen={isViewDialogOpen} onOpenChange={setIsViewDialogOpen} title="Детали платежа" description="Полная информация о платеже">
        {viewingPayment && (
          <div className="space-y-6 py-4">
            <DetailSection title="Информация о платеже" isDark={isDark}>
              <DetailItem label="ID платежа" isDark={isDark} mono breakAll>{viewingPayment.id}</DetailItem>
              <DetailItem label="Статус" isDark={isDark}>{getStatusBadge(viewingPayment.status)}</DetailItem>
              <DetailItem label="Провайдер" isDark={isDark}>{getProviderBadge(viewingPayment.provider)}</DetailItem>
              <DetailItem label="ID платежа провайдера" isDark={isDark} mono>{viewingPayment.provider_payment_id || '—'}</DetailItem>
              <DetailItem label="Сумма" isDark={isDark}><span className="text-lg font-bold">{formatPrice(viewingPayment.amount, viewingPayment.currency)}</span></DetailItem>
              <DetailItem label="ID заказа" isDark={isDark} mono breakAll>{viewingPayment.order_id}</DetailItem>
            </DetailSection>
            {viewingPayment.raw_response && (
              <div>
                <h3 className={cn('text-lg font-semibold mb-4', isDark ? 'text-white' : 'text-[#111111]')}>Сырой ответ</h3>
                <pre className={cn('p-4 rounded-lg overflow-auto text-xs', isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700')}>
                  {JSON.stringify(viewingPayment.raw_response, null, 2)}
                </pre>
              </div>
            )}
            <DetailSection title="Даты" isDark={isDark}>
              <DetailItem label="Создан" isDark={isDark}>{formatDate(viewingPayment.created_at)}</DetailItem>
            </DetailSection>
          </div>
        )}
      </ViewDialog>
    </div>
  );
}
